import React, { ReactElement } from 'react';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronsRight } from 'lucide-react';
import { IncidentView, SlaTarget, formatElapsed, formatTarget } from '../clientReporting/product/sources/incidents';
import { TimelineWindow, buildTimeline, tickStepHours } from '../clientReporting/product/sources/incidentTimeline';

/** One incident on a clock: the moments, and the two stretches the agreement gave us. */

/** A promise and the moment that answered it share one colour: green kept, red broken. */
const ANSWER_WEIGHT = 'opacity-90';
const RESOLVE_WEIGHT = 'opacity-40';

/** The verdict on a promise, in the hue its chevron is drawn in. */
const verdictTone = (met: boolean): string => (met ? 'bg-emerald-500' : 'bg-red-500');

/** How the band is stacked, in pixels from its floor: the clock low down, both promises on the line above. */
const AXIS_ABOVE_FLOOR = 12;
const SPAN_ROW = 24;
/** Both rules, drawn heavy enough to be a picture rather than a diagram of one. */
const STROKE = 4;
/** The thin stroke: a leader's weight, and a moment's. */
const STEM = 1;
/** The tick that closes off each end of a promise, centred on its line. */
const CAP_HEIGHT = 12;
/** Chevrons sit clear of the promise line: a mark resting on the rule reads as part of it. */
const CHEVRON_SIZE = 26;
/** The live mark's breath. `animate-pulse` is two seconds, which is a shade slow for a running clock. */
const PULSE = 'animate-pulse [animation-duration:1.4s] motion-reduce:animate-none';
/** Drawn to the same weight as the rules: the icon's stroke is in its own 24-unit box, not in pixels. */
const CHEVRON_STROKE = (STROKE * 24) / CHEVRON_SIZE;
const CHEVRON_CLEARANCE = 8;
/** A pause is exactly as tall as the rule it cuts, because it is a piece of that rule and not a region over it. */
const BAND_OVER = STROKE;
/** The hatching, shared with the key's swatch: a grey bar every seven pixels, upright and full height. */
const STRIPES =
  'repeating-linear-gradient(90deg, color-mix(in oklab, var(--color-muted-foreground) 55%, transparent) 0 3px, transparent 3px 7px)';

/** The marks on offer while the shape of a moment is being settled. */
export type MomentShape = 'hairline' | 'chevron' | 'triangle' | 'pin' | 'diamond';

/** The hairline's own weight and reach: the same 1px stroke a leader is drawn in, standing on the rule. */
const HAIRLINE_HEIGHT = 18;
/** The promise opens on a disc rather than a tick, and that disc is also the moment the incident came in. */
const ORIGIN_RADIUS = 5;

/** Each mark's box and where its point sits, so all four land the same height above the rule. */
const SHAPES: Record<MomentShape, { height: number; tip: number; clearance: number; across: number; down: number }> = {
  // `across` and `down` are the corner a leader lands on, measured off the mark's own box.
  chevron: {
    height: CHEVRON_SIZE,
    tip: CHEVRON_SIZE * 0.375 - STROKE / 2,
    clearance: CHEVRON_CLEARANCE,
    across: CHEVRON_SIZE * 0.25,
    down: CHEVRON_SIZE * 0.375,
  },
  // Stands on the rule: a gap under a line this thin would leave it floating over the hour it names.
  hairline: { height: HAIRLINE_HEIGHT, tip: 0, clearance: 0, across: 0, down: 0 },
  triangle: { height: 9, tip: 0, clearance: CHEVRON_CLEARANCE, across: 6.5, down: 0 },
  // A pin is read as standing on its hour, so its stem comes most of the way down to the rule.
  pin: { height: 15, tip: 0, clearance: 4, across: 4.5, down: 0 },
  diamond: { height: 11, tip: 0, clearance: CHEVRON_CLEARANCE, across: 5.5, down: 5.5 },
};

const momentRow = (shape: MomentShape): number => SPAN_ROW + STROKE / 2 + SHAPES[shape].clearance - SHAPES[shape].tip;
const bandHeight = (shape: MomentShape): number => momentRow(shape) + SHAPES[shape].height;

/** The hour of a tick, on the 24-hour clock the axis is read in. */
const tickHour = (ms: number, language: string): string =>
  new Intl.DateTimeFormat(language, { hour: '2-digit', minute: '2-digit', hourCycle: 'h23' }).format(new Date(ms));

/** A tick on a window that turns over into the next day, where the hour stops saying which day it is. */
const tickDay = (ms: number, language: string): string =>
  new Intl.DateTimeFormat(language, { day: 'numeric', month: 'short' }).format(new Date(ms));

const dateTime = (isoInstant: string, language: string): string =>
  new Intl.DateTimeFormat(language, {
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).format(new Date(isoInstant));

/** How close two moments can sit before their names are treated as a crowd. */
const CROWDED_WITHIN = 5;

/** How near an end a name has to be before that end is treated as a wall it cannot fan past. */
const AGAINST_THE_EDGE = 5;

/** A name sits over its own moment, or off to one side of the crowd it is in. */
type Side = 'centre' | 'left' | 'right';

/** Where each name goes: over its moment where there is room, fanned either side of a crowd where there is not. */
export const place = (positions: number[]): { side: Side; row: number; anchor?: number }[] => {
  const placed: { side: Side; row: number; anchor?: number }[] = [];
  let from = 0;

  while (from < positions.length) {
    let to = from + 1;
    while (to < positions.length && positions[to] - positions[to - 1] < CROWDED_WITHIN) to += 1;
    const crowd = to - from;

    if (crowd === 1) {
      placed.push({ side: 'centre', row: 0 });
    } else {
      // Each end asked of the mark that would hang off it: the first fans left, the last fans right.
      let oneSided: Side | null = null;
      if (positions[from] < AGAINST_THE_EDGE) oneSided = 'right';
      if (positions[to - 1] > 100 - AGAINST_THE_EDGE) oneSided = 'left';
      // Every name in the crowd starts from the same moment, so none lies under another's stem.
      const anchor = oneSided === 'right' ? positions[to - 1] : positions[from];
      for (let index = 0; index < crowd; index += 1) {
        placed.push(
          oneSided
            ? { side: oneSided, row: index, anchor }
            : { side: index % 2 ? 'right' : 'left', row: Math.floor(index / 2) },
        );
      }
    }
    from = to;
  }

  return placed;
};

/** One promised stretch, a rule ticked at each end: open when it came in, closed when the promise ran out. */
const Span = ({
  from,
  to,
  open,
  opening,
  tone,
  weight,
}: {
  from: number;
  to: number;
  /** The promise outlasts the drawn window, so it has no end to draw. */
  open: boolean;
  /** Draws the tick the rule opens on, which belongs to the segment that holds the shared start. */
  opening?: boolean;
  /** The hue of the moment this promise is judged by. */
  tone: string;
  /** Which of the two promises on the rule this is, when both are the same colour. */
  weight: string;
}): ReactElement => {
  const cap = (at: number): ReactElement => (
    <span
      className={cx('absolute -translate-x-1/2 rounded-full', tone)}
      style={{
        left: `${at}%`,
        bottom: `${SPAN_ROW - CAP_HEIGHT / 2}px`,
        width: `${STROKE}px`,
        height: `${CAP_HEIGHT}px`,
      }}
    />
  );

  /* The shared start, as a node on the rule. See `ORIGIN_RADIUS`: it is the "came in" mark as well. */
  const node = (at: number): ReactElement => (
    <span
      className={cx('absolute -translate-x-1/2 rounded-full', tone)}
      style={{
        left: `${at}%`,
        bottom: `${SPAN_ROW - ORIGIN_RADIUS}px`,
        width: `${ORIGIN_RADIUS * 2}px`,
        height: `${ORIGIN_RADIUS * 2}px`,
      }}
    />
  );

  return (
    // Full width, so percentages resolve against the clock's axis; an inset-y-0 box collapses them left.
    <div className={cx('pointer-events-none absolute inset-0', weight)}>
      <span
        // Square where it meets its tick: two rounded ends leave a wedge that reads as a gap.
        className={cx('absolute', opening ? 'rounded-full' : 'rounded-r-full', tone)}
        style={{
          // Half a stroke up against the tick's outer edge, because a tick is centred on its moment.
          left: opening ? `${from}%` : `calc(${from}% + ${STROKE / 2}px)`,
          width: opening ? `${Math.max(to - from, 0)}%` : `calc(${Math.max(to - from, 0)}% - ${STROKE / 2}px)`,
          height: `${STROKE}px`,
          bottom: `${SPAN_ROW - STROKE / 2}px`,
        }}
      />
      {opening && node(from)}
      {!open && cap(to)}
    </div>
  );
};

/** A stretch of the clock that was somebody else's to move. */
const PauseBand = ({ from, to, title }: { from: number; to: number; title?: string }): ReactElement => (
  <span
    title={title}
    className="absolute bg-card"
    style={{
      left: `${from}%`,
      width: `${Math.max(to - from, 0)}%`,
      bottom: `${SPAN_ROW - BAND_OVER / 2}px`,
      height: `${BAND_OVER}px`,
      backgroundImage: STRIPES,
    }}
  />
);

/** The glyph for a moment, doubled or laid on its side when it falls off the drawn window. */
const glyph = (shape: MomentShape, beyond: boolean, className: string, style: React.CSSProperties): ReactElement => {
  const svg = (width: number, height: number, viewBox: string, path: ReactElement): ReactElement => (
    <svg
      aria-hidden
      width={width}
      height={height}
      viewBox={viewBox}
      fill="currentColor"
      className={className}
      style={style}
    >
      {path}
    </svg>
  );

  /** Doubled and leaning right: this happened past the last hour drawn, not on it. */
  if (shape === 'hairline')
    return beyond ? (
      svg(
        11,
        HAIRLINE_HEIGHT,
        '0 0 11 18',
        <>
          <path d="M0 18 4 0h1L1 18Z" />
          <path d="M6 18 10 0h1L7 18Z" />
        </>,
      )
    ) : (
      /** A plain box, not a one-pixel SVG. */
      <span
        aria-hidden
        className={className}
        style={{ ...style, width: `${STEM}px`, height: `${HAIRLINE_HEIGHT}px`, backgroundColor: 'currentColor' }}
      />
    );

  if (shape === 'triangle')
    return beyond
      ? svg(15, 12, '0 0 15 12', <path d="M0 0l6 6-6 6Z M8 0l6 6-6 6Z" />)
      : svg(13, 9, '0 0 13 9', <path d="M6.5 9 0 0h13Z" />);

  if (shape === 'pin')
    return beyond
      ? svg(
          15,
          9,
          '0 0 15 9',
          <>
            <rect x="0" y="3.25" width="9" height="2" rx="1" />
            <circle cx="10.5" cy="4.5" r="4.5" />
          </>,
        )
      : svg(
          9,
          15,
          '0 0 9 15',
          <>
            <circle cx="4.5" cy="4.5" r="4.5" />
            <rect x="3.5" y="7" width="2" height="8" rx="1" />
          </>,
        );

  if (shape === 'diamond')
    return beyond
      ? svg(20, 11, '0 0 20 11', <path d="M5.5 0 11 5.5 5.5 11 0 5.5Z M14.5 0 20 5.5 14.5 11 9 5.5Z" />)
      : svg(11, 11, '0 0 11 11', <path d="M5.5 0 11 5.5 5.5 11 0 5.5Z" />);

  const Icon = beyond ? ChevronsRight : ChevronDown;
  return <Icon aria-hidden strokeWidth={CHEVRON_STROKE} size={CHEVRON_SIZE} className={className} style={style} />;
};

const Moment = ({
  at,
  tone,
  beyond,
  live,
  shape,
}: {
  at: number;
  tone: string;
  beyond?: boolean;
  /** This one is where the clock has got to, not something that happened. */
  live?: boolean;
  shape: MomentShape;
}): ReactElement => {
  /** Placed by the same arithmetic as its leader, half a stroke back, not by a transform. */
  const straddles = shape === 'hairline' && !beyond;

  return glyph(
    shape,
    !!beyond,
    cx('absolute', beyond && 'right-0', !beyond && !straddles && '-translate-x-1/2', tone, live && PULSE),
    {
      bottom: `${momentRow(shape)}px`,
      ...(beyond ? {} : { left: straddles ? `calc(${at}% - ${STEM / 2}px)` : `${at}%` }),
    },
  );
};

/** The line height a name occupies. */
const LABEL_LINE = 16;
/** An arm of this length with the same air at both ends, off the chevron and off the name. */
const LEADER_ARM = 14;
const LEADER_AIR = 3;

/** How far above the path a chevron's arm reaches, measured off its own box: where a leader stops. */
const CHEVRON_ARM_CAP = STROKE / 2;

/** What a moment is called, over the band. */
const MomentLabel = ({
  at,
  anchor = at,
  row,
  side = 'centre',
  text,
  title,
  tone,
  leaderTone,
  shape = 'hairline',
  live,
}: {
  at: number;
  /** Where the words stand when not over the moment: the far edge of the crowd it is stacked in. */
  anchor?: number;
  row: number;
  /** Left out by the hours on the axis, which are always over their own tick. */
  side?: Side;
  text: string;
  title?: string;
  tone?: string;
  /** The chevron's hue, as a fill: what the leader is drawn in. */
  leaderTone?: string;
  /** The mark this name is tied to, which is what the leader has to land on. */
  shape?: MomentShape;
  /** Whether the mark this leads to is the running clock, and so breathing. */
  live?: boolean;
}): ReactElement => {
  /** A name with room stands over its own mark, its line stopping under the word. */
  const alone = side === 'centre';
  const centred = alone && at >= AGAINST_THE_EDGE && at <= 100 - AGAINST_THE_EDGE;

  // The arm at its highest point, then the leader's length and the same air before the words.
  const tip = SHAPES[shape].across * (side === 'left' ? -1 : 1);
  const reach = Math.abs(tip) + LEADER_ARM + LEADER_AIR;
  /** Straight down onto that point, stopping as short as the name stands off, never above the band. */
  const drop = Math.max(0, SHAPES[shape].down - CHEVRON_ARM_CAP - LEADER_AIR);
  let edge: { left: number | string } | { right: string } = { left: `${at}%` };
  if (alone && at < AGAINST_THE_EDGE) edge = { left: 0 };
  if (alone && at > 100 - AGAINST_THE_EDGE) edge = { right: `${100 - at}%` };
  if (side === 'left') edge = { right: `calc(${100 - anchor}% + ${reach}px)` };
  if (side === 'right') edge = { left: `calc(${anchor}% + ${reach}px)` };
  // The extra the arm covers to reach back: zero unless stacked, where it is the crowd's width.
  const spread = Math.abs(anchor - at);

  /** Level with the name's own line, so a crowded name's arm comes in beside its words. */
  const along = row * LABEL_LINE + LABEL_LINE / 2;

  return (
    <>
      <span
        className={cx(
          'absolute bottom-0 whitespace-nowrap text-[11px] leading-4',
          tone ?? 'text-foreground',
          centred && '-translate-x-1/2',
        )}
        style={{ ...edge, marginBottom: `${row * LABEL_LINE}px` }}
        title={title}
      >
        {text}
      </span>
      {!alone && (
        <>
          {/* Up through the arm's own line rather than up to it: the two are a hairline each, and the
              corner square where they cross belongs to neither, which left a white notch in the elbow. */}
          <span
            aria-hidden
            className={cx('absolute', leaderTone, live && PULSE)}
            style={{
              left: `calc(${at}% + ${tip}px - ${STEM / 2}px)`,
              width: `${STEM}px`,
              bottom: `-${drop}px`,
              height: `${along + drop + 1}px`,
            }}
          />
          {/* Half a pixel back, so the hairline straddles the arm's point rather than starting on it.
              Set by its left edge, one leader leans into its chevron and the mirrored one leans out of
              its own by the same half pixel — which is small, and is exactly what reads as uneven. */}
          <span
            aria-hidden
            className={cx('absolute h-px', leaderTone, live && PULSE)}
            style={{
              left:
                side === 'left'
                  ? `calc(${anchor}% - ${Math.abs(tip) + LEADER_ARM}px)`
                  : `calc(${at}% + ${tip}px - ${STEM / 2}px)`,
              width: `calc(${spread}% + ${LEADER_ARM + STEM / 2}px)`,
              bottom: `${along}px`,
            }}
          />
        </>
      )}
    </>
  );
};

/** What became of one promise, or nothing while nothing has become of it. */
const outcomeOf = (
  t: (key: string, values?: Record<string, string>) => string,
  window: TimelineWindow,
  done: boolean,
  which: 'Answer' | 'Resolve',
): string => {
  // Counted against the clock, so an unanswered incident keeps counting while the page is open.
  if (window.overBy !== null) {
    const time = formatElapsed(window.elapsed);
    if (!done) return t('incidents.timelineRunning', { time });
    return t(which === 'Answer' ? 'incidents.timelineAnsweredIn' : 'incidents.timelineResolvedIn', { time });
  }
  if (done) return t(which === 'Answer' ? 'incidents.timelineAnsweredInTime' : 'incidents.timelineResolvedInTime');
  return '';
};

/** One promise in the key: what was promised, then what became of it. */
const Key = ({
  tone,
  weight,
  name,
  outcome,
  window,
  dueAt,
}: {
  /** The span's own colour and weight, so the row and the picture match without a word for it. */
  tone: string;
  weight: string;
  name: string;
  /** What became of the promise, in the words its own state is said in. */
  outcome: string;
  window: TimelineWindow;
  /** When the promise ran out, said only when that moment is off the drawn window. */
  dueAt: string;
}): ReactElement => {
  const { t } = useTranslation();

  return (
    <>
      <span className="flex items-center gap-2">
        {/* The span in miniature — a rule between two end ticks — so the row and the picture are the
            same mark at two sizes. */}
        <span aria-hidden className={cx('relative block h-3 w-6 shrink-0', weight)}>
          <span className={cx('absolute inset-x-0 top-1/2 h-1 -translate-y-1/2 rounded-full', tone)} />
          {/* Opens on a node and closes on a tick, the same as the rule it stands for: both promises
              start where the incident came in, and only their far ends are deadlines. */}
          <span className={cx('absolute left-0 top-1/2 size-2.5 -translate-y-1/2 rounded-full', tone)} />
          <span className={cx('absolute inset-y-0 right-0 w-1 rounded-full', tone)} />
        </span>
        <span className="text-foreground">{name}</span>
      </span>
      <span className="flex items-center gap-2">
        <span
          className={cx(
            'font-medium',
            window.met ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400',
          )}
        >
          {outcome}
        </span>
        {/* Only when the deadline itself is off the drawn clock, where the picture cannot say when it is. */}
        {window.clamped && <span className="text-muted-foreground">{t('incidents.timelineDue', { at: dueAt })}</span>}
      </span>
    </>
  );
};

const IncidentTimeline = ({
  incident,
  shape = 'hairline',
}: {
  incident: IncidentView & { target: SlaTarget };
  /** Which mark a moment is drawn as, while the PMs settle on one. */
  shape?: MomentShape;
}): ReactElement => {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const timeline = buildTimeline(incident);
  // Past half a day the ticks are hours apart enough that the hour alone stops saying which day it is.
  const isDayScale = tickStepHours((timeline.toMs - timeline.fromMs) / 3600000) >= 24;

  /** What actually happened, in the order it happened. */
  const happened = [
    {
      at: timeline.start.at,
      beyond: false,
      text: t('incidents.timelineCameIn'),
      title: dateTime(incident.reportedAt, language),
      /** The one moment drawn in a promise's colour rather than in ink. */
      tone: verdictTone(timeline.responseWindow.met).replace('bg-', 'text-'),
      leaderTone: verdictTone(timeline.responseWindow.met),
      labelTone: 'text-foreground',
    },
    ...(timeline.responded && incident.respondedAt
      ? [
          {
            at: timeline.responded.at,
            beyond: timeline.responded.offWindow,
            text: t('incidents.timelineAnswered'),
            title: dateTime(incident.respondedAt, language),
            tone: timeline.responseWindow.met ? 'text-emerald-500' : 'text-red-500',
            leaderTone: timeline.responseWindow.met ? 'bg-emerald-500' : 'bg-red-500',
            labelTone: 'text-foreground',
          },
        ]
      : []),
    ...(incident.resolvedAt
      ? [
          {
            at: timeline.end.at,
            beyond: timeline.end.offWindow,
            text: t('incidents.timelineResolved'),
            title: dateTime(incident.resolvedAt, language),
            tone: timeline.resolutionWindow.met ? 'text-emerald-500' : 'text-red-500',
            leaderTone: timeline.resolutionWindow.met ? 'bg-emerald-500' : 'bg-red-500',
            labelTone: 'text-foreground',
          },
        ]
      : // An open incident's last mark is "now": a position rather than something that happened, and it
        // moves.
        [
          {
            at: timeline.end.at,
            beyond: timeline.end.offWindow,
            text: t('incidents.timelineNow'),
            title: t('incidents.timelineStillRunning'),
            tone: 'text-foreground',
            leaderTone: 'bg-foreground',
            labelTone: 'text-muted-foreground',
            live: true,
          },
        ]),
  ];

  const placed = place(happened.map(entry => entry.at));
  const rows = Math.max(...placed.map(entry => entry.row));

  return (
    <section className="rounded-xl border border-border bg-card p-6 print:break-inside-avoid">
      <div className="mb-6">
        <h2 className="text-lg font-bold text-foreground">{t('incidents.timelineHeading')}</h2>
        <p className="text-xs text-muted-foreground">{t('incidents.timelineMeaning')}</p>
      </div>

      {/* One column, so the labels, the band and the hours all measure the same width and a line at
          40% means 40% in every row of the card. */}
      <div className="px-2">
        {/* What happened, sitting straight on the band below it. */}
        <div className="relative" style={{ height: `${LABEL_LINE * (rows + 1)}px` }}>
          {happened.map((entry, index) => (
            <MomentLabel
              key={entry.text}
              at={entry.at}
              anchor={placed[index].anchor}
              row={placed[index].row}
              side={placed[index].side}
              text={entry.text}
              title={entry.title}
              tone={entry.labelTone}
              leaderTone={entry.leaderTone}
              shape={shape}
              live={'live' in entry && entry.live}
            />
          ))}
        </div>

        {/* The clock, with a promise on each line over it and the chevrons pointing down at them. Not a
            box — the hours are notches on a line, and boxing them turns a timeline into a table of
            empty cells. */}
        <div className="relative" style={{ height: `${bandHeight(shape)}px` }}>
          <div
            className="absolute inset-x-0 rounded-full bg-border"
            style={{ height: `${STROKE}px`, bottom: `${AXIS_ABOVE_FLOOR - STROKE / 2}px` }}
          />

          {timeline.ticks.map(tick => (
            <span
              key={tick.atMs}
              className={cx(
                'absolute rounded-full bg-border',
                // A little taller where the day turns over, which is the tick that carries a date.
                tick.isMidnight ? 'h-4' : 'h-2.5',
              )}
              style={{
                left: `${tick.at}%`,
                width: `${STROKE}px`,
                bottom: `${AXIS_ABOVE_FLOOR - (tick.isMidnight ? 8 : 5)}px`,
              }}
            />
          ))}

          {/* One rule in two segments: the answer holds the head and the shared opening tick, the
              resolve carries on from the answer's tick to its own. */}
          <Span
            from={timeline.start.at}
            to={timeline.responseWindow.at}
            open={timeline.responseWindow.clamped}
            tone={verdictTone(timeline.responseWindow.met)}
            weight={ANSWER_WEIGHT}
            opening
          />
          <Span
            from={timeline.responseWindow.at}
            to={timeline.resolutionWindow.at}
            open={timeline.resolutionWindow.clamped}
            tone={verdictTone(timeline.resolutionWindow.met)}
            weight={RESOLVE_WEIGHT}
          />

          {/* Laid over the promises rather than beside them: a pause is a stretch of the same clock,
              and what it explains is why the rule reaches further than the promise it was made against. */}
          {timeline.pauses.map(pause => (
            <PauseBand
              key={pause.atMs}
              from={pause.from}
              to={pause.to}
              title={pause.reason ?? t('incidents.timelinePaused', { length: formatElapsed(pause.minutes) })}
            />
          ))}

          {happened.map(entry => (
            <Moment
              key={entry.text}
              at={entry.at}
              tone={entry.tone}
              beyond={entry.beyond}
              live={'live' in entry && entry.live}
              shape={shape}
            />
          ))}
        </div>

        {/* The hours. */}
        <div className="relative mt-1 h-4">
          {timeline.ticks.map(tick => (
            <MomentLabel
              key={tick.atMs}
              at={tick.at}
              row={0}
              text={isDayScale || tick.isMidnight ? tickDay(tick.atMs, language) : tickHour(tick.atMs, language)}
              tone="tabular-nums text-muted-foreground"
            />
          ))}
        </div>
      </div>

      {/* What became of each promise, in words. The picture says where the moments fell; this says what
          that came to, which is the sentence a report is written from. */}
      {/* Two columns: the promise, then what became of it. A column rather than a run of words, so the
          two outcomes start at the same place and the pair can be read down rather than across. */}
      <div className="mt-5 grid w-fit grid-cols-[auto_auto] items-center gap-x-4 gap-y-1.5 text-[11px]">
        <Key
          tone={verdictTone(timeline.responseWindow.met)}
          weight={ANSWER_WEIGHT}
          name={t('incidents.timelineAnswerWindow', { target: formatTarget(timeline.responseWindow.minutes / 60) })}
          outcome={outcomeOf(t, timeline.responseWindow, !!timeline.responded, 'Answer')}
          window={timeline.responseWindow}
          dueAt={dateTime(new Date(timeline.responseWindow.atMs).toISOString(), language)}
        />
        <Key
          tone={verdictTone(timeline.resolutionWindow.met)}
          weight={RESOLVE_WEIGHT}
          name={t('incidents.timelineResolveWindow', { target: formatTarget(timeline.resolutionWindow.minutes / 60) })}
          outcome={outcomeOf(t, timeline.resolutionWindow, !timeline.isOpen, 'Resolve')}
          window={timeline.resolutionWindow}
          dueAt={dateTime(new Date(timeline.resolutionWindow.atMs).toISOString(), language)}
        />

        {/* Only when something was waited on. A row saying "nothing" on every other incident is the key
            explaining a mark that is not in the picture. */}
        {timeline.pausedMinutes > 0 && (
          <>
            <span className="flex items-center gap-2">
              {/* The band in miniature, so the row and the picture are the same mark at two sizes. */}
              <span
                aria-hidden
                className="block h-1 w-6 shrink-0 rounded-full bg-card"
                style={{ backgroundImage: STRIPES }}
              />
              <span className="text-foreground">{t('incidents.timelinePausedName')}</span>
            </span>
            {/* Said as what it did rather than as a verdict: the hours are neither kept nor broken, they
                are the ones the promise was never running through. */}
            <span className="text-muted-foreground">
              {t('incidents.timelinePausedTotal', { time: formatElapsed(timeline.pausedMinutes) })}
            </span>
          </>
        )}
      </div>
    </section>
  );
};

export default IncidentTimeline;
